import { Button, Option, Select, Text } from '@shared/components';
import { ActionTimelineEvent } from '@shared/components/events-timeline/components/action-timeline-event';
import {
  ScheduleMethod,
  UpdatePlaceScheduleMethodInput,
  useUpdatePlaceScheduleMethodMutation,
} from '@shared/generated/graphql';
import { titleCase } from '@shared/utils/stringification/casing';
import { toPortalLabel } from 'clerk_common/features/scheduleUtil';
import { ScheduleStop } from 'clerk_common/types';
import { useState } from 'react';
import { humanizeDate } from '../../util';
import { StopProps } from './Stop';

export function UnknownStop({ stop, refetchShipment }: StopProps) {
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const mutation = useUpdatePlaceScheduleMethodMutation();
  const [updateScheduleMethod, { loading }] = mutation;

  function handleSelectChange(value: string | null) {
    setSelectedOption(value);
  }

  async function handleSave() {
    const method = toMethod(selectedOption);
    if (!method) return;
    const input = toInput(stop, method);
    await updateScheduleMethod({ variables: { input } });
    refetchShipment();
  }

  const options = getOptions();

  return (
    <ActionTimelineEvent type={stop.stopType}>
      <div className="mb-4 ml-2 mt-1 text-gray-700">
        <Text isHeavy type="body-xs">
          {stop.locationLabel}
        </Text>
        <Text type="body-xs">{stop.address.addressOne}</Text>
        <Text type="body-xs">{humanizeDate(stop.day)}</Text>
        <Select
          placeholder="Choose a scheduling method..."
          className="mt-2"
          value={selectedOption}
          onChange={handleSelectChange}
        >
          {options.map((option) => (
            <Option key={option} value={option}>
              {option}
            </Option>
          ))}
        </Select>
        <div>
          <Button
            size="small"
            variant="secondary"
            className="ml-auto mt-2"
            isDisabled={loading}
            onPress={handleSave}
          >
            Save
          </Button>
        </div>
      </div>
    </ActionTimelineEvent>
  );
}

function getOptions() {
  const values: ScheduleMethod[] = Object.values(ScheduleMethod).filter(
    (v) => v !== ScheduleMethod.EMAIL
  );
  values.splice(0, 0, ScheduleMethod.EMAIL);
  const options = values.map((v) => toLabel(v));
  return options;
}

function toLabel(method: ScheduleMethod) {
  switch (method) {
    case ScheduleMethod.EMAIL:
      return titleCase(method);
    default:
      return toPortalLabel(method);
  }
}

function toMethod(s: string | null): ScheduleMethod | null {
  if (s == null) return null;
  for (const method of Object.values(ScheduleMethod)) {
    const label = toLabel(method);
    if (label === s) {
      return method;
    }
  }
  return null;
}

function toInput(
  stop: ScheduleStop,
  method: ScheduleMethod
): UpdatePlaceScheduleMethodInput {
  const input: UpdatePlaceScheduleMethodInput = {
    id: stop.placeId,
    method,
  };
  return input;
}
